import { useCallback, useEffect, useMemo, useState, type Key } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import {
  DisconnectOutlined,
  EditOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { useSearchParams } from 'react-router-dom'
import { useToast } from '@/contexts/ToastContext'
import { BulkDisconnectProgressModal } from '@/features/platform/components/BulkDisconnectProgressModal'
import { ClientRefreshModal } from '@/features/platform/components/ClientRefreshModal'
import { CreatePlatformModal } from '@/features/platform/components/CreatePlatformModal'
import { EditPlatformModal } from '@/features/platform/components/EditPlatformModal'
import { ManualRefreshModal } from '@/features/platform/components/ManualRefreshModal'
import {
  invalidateCapacityPlannerSettingsQuery,
  invalidatePlatformListQueries,
  useCreatePlatformMutation,
  useDeletePlatformMutation,
  useDisconnectPlatformsMutation,
  useInitPlatformEditMutation,
  useManualRefreshMutation,
  usePlatformConnections,
  useUpdatePlatformMutation,
} from '@/features/platform/hooks'
import type { PlatformConnectionRow, PlatformStatus, PlatformType } from '@/features/platform/types'
import {
  formatPlatformDateTime,
  getXeroBulkSsoUrl,
  isDisconnectAllowed,
  isManualRefreshAllowed,
  openPopup,
  showAddXeroConnection,
} from '@/features/platform/utils'
import { resolveEnvironmentName } from '@/lib/environment'
import { useAuthSession } from '@/lib/session'
import { UserTypes } from '@/types/authorization'
import { useAdvicePrepTrialLimit } from '@/features/adviceprep/hooks'
import AdvicePrepTrialLimitAlert from '@/features/adviceprep/AdvicePrepTrialLimitAlert'
import { advicePrepTrialLimitHint } from '@/features/adviceprep/trialLimit'

type EditModalState = {
  open: boolean
  row: PlatformConnectionRow | null
}

type RefreshModalState = {
  open: boolean
  row: PlatformConnectionRow | null
}

type CreateModalState = {
  open: boolean
}

const statusColor: Record<PlatformStatus, string> = {
  Pending: 'gold',
  Refreshing: 'cyan',
  Refreshed: 'green',
  Failed: 'red',
  Connected: 'blue',
}

const statusDescriptions: { status: PlatformStatus; description: string }[] = [
  { status: 'Pending', description: 'Platform created and awaiting authentication or first connection sync.' },
  { status: 'Connected', description: 'Platform authenticated and connected successfully.' },
  { status: 'Refreshing', description: 'Platform is actively refreshing data.' },
  { status: 'Refreshed', description: 'Latest refresh completed successfully.' },
  { status: 'Failed', description: 'Most recent refresh attempt failed.' },
]

const typeOptions: { value: PlatformType; label: string }[] = [
  { value: 'Xero', label: 'Xero Accounting' },
  { value: 'XPM', label: 'Xero Practice Manager' },
  { value: 'WorkGuru', label: 'WorkGuru' },
  { value: 'BRWFM', label: 'BlueRock Workflow Max' },
  { value: 'Karbon', label: 'Karbon' },
  { value: 'FYI', label: 'FYI' },
  { value: 'MYOB', label: 'MYOB' },
  { value: 'QuickBooks', label: 'QuickBooks' },
]

export default function Platform() {
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const authSession = useAuthSession()
  const [searchParams] = useSearchParams()
  const activeClientId = authSession?.currentClientId || ''
  const isDiAdmin = authSession?.currentUserTypeId === UserTypes.Admin

  const [tableData, setTableData] = useState<PlatformConnectionRow[]>([])
  const [nameFilter, setNameFilter] = useState('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState<PlatformType | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<PlatformStatus | 'all'>('all')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25 })
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const [bulkProgressOpen, setBulkProgressOpen] = useState(false)
  const [bulkProgressIds, setBulkProgressIds] = useState<string[]>([])
  const [bulkProgressIsBulk, setBulkProgressIsBulk] = useState(false)
  const [bulkProgressIdToName, setBulkProgressIdToName] = useState<Record<string, string>>({})
  const [createState, setCreateState] = useState<CreateModalState>({ open: false })
  const [editState, setEditState] = useState<EditModalState>({ open: false, row: null })
  const [refreshState, setRefreshState] = useState<RefreshModalState>({ open: false, row: null })
  const [clientRefreshOpen, setClientRefreshOpen] = useState(false)
  const envName = resolveEnvironmentName()
  const advicePrepTrial = useAdvicePrepTrialLimit(activeClientId)

  const filters = {
    name: nameFilter || undefined,
    companyName: companyFilter || undefined,
    type: typeFilter,
    status: statusFilter,
  }

  const { data: rows = [], isLoading, isFetching, refetch } = usePlatformConnections({
    clientId: activeClientId,
    ...filters,
  })
  const initEditMutation = useInitPlatformEditMutation()
  const createPlatformMutation = useCreatePlatformMutation(activeClientId, filters)
  const updatePlatformMutation = useUpdatePlatformMutation(activeClientId, filters)
  const disconnectMutation = useDisconnectPlatformsMutation(activeClientId, filters)
  const deletePlatformMutation = useDeletePlatformMutation(activeClientId, filters)
  const manualRefreshMutation = useManualRefreshMutation(activeClientId, filters)
  const platformTableLoading = isLoading || isFetching || disconnectMutation.isPending

  const createTypeOptions = useMemo(
    () => (['prod', 'production', 'portal'].includes(envName) ? typeOptions.filter((option) => option.value !== 'Xero') : typeOptions),
    [envName]
  )

  useEffect(() => {
    setTableData(rows)
    setSelectedRowKeys([])
  }, [rows])

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }))
  }, [nameFilter, companyFilter, typeFilter, statusFilter])

  useEffect(() => {
    const oauthCode = searchParams.get('code')
    if (oauthCode && typeof window !== 'undefined' && window.opener) {
      window.opener.postMessage({ code: oauthCode }, window.location.origin)
      window.close()
    }
  }, [searchParams])

  const disconnectableIds = useMemo(
    () => tableData.filter(isDisconnectAllowed).map((row) => row.id),
    [tableData]
  )

  const canSelectAllDisconnect = disconnectableIds.length > 0

  const toggleSelectAllDisconnect = useCallback((checked: boolean) => {
    if (!checked) {
      setSelectedRowKeys([])
      return
    }
    setSelectedRowKeys(disconnectableIds)
  }, [disconnectableIds])

  const openEdit = useCallback(async (row: PlatformConnectionRow) => {
    try {
      const latest = await initEditMutation.mutateAsync(row.id)
      const resolved = latest || row
      setEditState({ open: true, row: resolved })
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to open edit modal'
      addToast(message, 'error')
    }
  }, [addToast, initEditMutation])

  const closeEdit = () => {
    setEditState({ open: false, row: null })
  }

  const openCreate = () => {
    setCreateState({ open: true })
  }

  const closeCreate = () => {
    setCreateState({ open: false })
  }

  const openManualRefresh = (row: PlatformConnectionRow) => {
    setRefreshState({ open: true, row })
  }

  const startBulkDisconnect = useCallback(
    (ids: string[], isBulk: boolean) => {
      const idToName: Record<string, string> = {}
      tableData.forEach((row) => {
        idToName[row.id] = row.name
      })
      setBulkProgressIds(ids)
      setBulkProgressIsBulk(isBulk)
      setBulkProgressIdToName(idToName)
      setBulkProgressOpen(true)
    },
    [tableData]
  )

  const handleBulkDisconnectClick = useCallback(() => {
    const eligible = selectedRowKeys
      .filter((key) => disconnectableIds.includes(String(key)))
      .map(String)
    if (eligible.length === 0) {
      addToast('No eligible platforms selected for disconnect', 'warning')
      return
    }
    Modal.confirm({
      title: 'Disconnect platforms',
      content: 'Are you sure you want to disconnect these platforms?',
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk: () => {
        Modal.confirm({
          title: 'Delete platforms',
          content: 'Do you want to delete these platforms after disconnecting?',
          okText: 'Yes',
          cancelText: 'No',
          onOk: () => startBulkDisconnect(eligible, true),
          onCancel: () => startBulkDisconnect(eligible, false),
        })
      },
    })
  }, [addToast, disconnectableIds, selectedRowKeys, startBulkDisconnect])

  const closeBulkProgress = useCallback(() => {
    setBulkProgressOpen(false)
    setSelectedRowKeys([])
    invalidatePlatformListQueries(queryClient, activeClientId)
    invalidateCapacityPlannerSettingsQuery(queryClient, activeClientId)
    void refetch()
  }, [activeClientId, queryClient, refetch])

  const columns: ColumnsType<PlatformConnectionRow> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (_, record) => (
          <button
            type="button"
            onClick={() => void openEdit(record)}
            className="font-medium text-gray-900 hover:text-brand-600 text-left"
          >
            {record.name}
          </button>
        ),
      },
      {
        title: 'Company Name',
        dataIndex: 'companyName',
        key: 'companyName',
        sorter: (a, b) => a.companyName.localeCompare(b.companyName),
        render: (value: string) => value || '—',
      },
      {
        title: 'Platform Type',
        dataIndex: 'type',
        key: 'type',
      },
      {
        title: 'Last Refreshed',
        dataIndex: 'lastSuccessfulRefresh',
        key: 'lastSuccessfulRefresh',
        render: (value: string | null) => (value ? formatPlatformDateTime(value) : '—'),
      },
      {
        title: (
          <span className="inline-flex items-center gap-1">
            Refresh Status
            <Tooltip
              title={
                <div className="py-1">
                  {statusDescriptions.map((item) => (
                    <div key={item.status} className="mb-2 last:mb-0">
                      <div className="font-medium">{item.status}</div>
                      <div className="pl-3 text-xs leading-5">{item.description}</div>
                    </div>
                  ))}
                </div>
              }
            >
              <InfoCircleOutlined className="text-gray-500" />
            </Tooltip>
          </span>
        ),
        dataIndex: 'status',
        key: 'status',
        render: (value: PlatformStatus, record) => (
          <Tag color={statusColor[value]}>
            {value}
            {value === 'Refreshing' && record.stage ? ` - ${record.stage}` : ''}
          </Tag>
        ),
      },
      {
        title: 'Manual Refresh',
        key: 'manualRefresh',
        width: 80,
        render: (_, record) => (
          <Tooltip
            title={
              isManualRefreshAllowed(record)
                ? 'Manual refresh'
                : 'Only Xero platforms not currently refreshing can be manually refreshed'
            }
          >
            <Button
              type="text"
              icon={<SyncOutlined />}
              aria-label="Manual refresh"
              disabled={!isManualRefreshAllowed(record)}
              onClick={() => openManualRefresh(record)}
            />
          </Tooltip>
        ),
      },
      {
        title: (
          <div className="flex flex-col gap-1">
            <span>Disconnect</span>
            <Checkbox
              checked={
                canSelectAllDisconnect &&
                selectedRowKeys.length > 0 &&
                disconnectableIds.every((id) => selectedRowKeys.includes(id))
              }
              indeterminate={
                selectedRowKeys.length > 0 &&
                disconnectableIds.some((id) => selectedRowKeys.includes(id)) &&
                !disconnectableIds.every((id) => selectedRowKeys.includes(id))
              }
              disabled={!canSelectAllDisconnect}
              onChange={(e) => toggleSelectAllDisconnect(e.target.checked)}
            >
              Select All
            </Checkbox>
          </div>
        ),
        key: 'disconnect',
        width: 120,
        render: (_, record) =>
          isDisconnectAllowed(record) ? (
            <Checkbox
              checked={selectedRowKeys.includes(record.id)}
              onChange={(event) => {
                setSelectedRowKeys((prev) => {
                  if (event.target.checked) return [...new Set([...prev, record.id])]
                  return prev.filter((id) => id !== record.id)
                })
              }}
            />
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        title: '',
        key: 'actions',
        width: 88,
        render: (_, record) => (
          <Space size={4}>
            <Tooltip title="Edit platform">
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label="Edit platform"
                onClick={() => void openEdit(record)}
              />
            </Tooltip>
            <Tooltip title="Disconnect platform">
              <Button
                type="text"
                danger
                icon={<DisconnectOutlined />}
                aria-label="Disconnect platform"
                disabled={!isDisconnectAllowed(record)}
                loading={disconnectMutation.isPending}
                onClick={async () => {
                  try {
                    await disconnectMutation.mutateAsync({ platformIds: [record.id], isBulk: false })
                    addToast(`${record.name} disconnected`, 'warning')
                  } catch (e) {
                    const message = e instanceof Error ? e.message : 'Unable to disconnect platform'
                    addToast(message, 'error')
                  }
                }}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [
      addToast,
      canSelectAllDisconnect,
      disconnectMutation,
      disconnectableIds,
      openEdit,
      selectedRowKeys,
      toggleSelectAllDisconnect,
    ]
  )

  const onTableChange = (nextPagination: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current ?? prev.current,
      pageSize: nextPagination.pageSize ?? prev.pageSize,
    }))
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white via-white to-emerald-50/35">
      <div
        className="pointer-events-none absolute -top-16 -right-16 h-44 w-44 rounded-full bg-emerald-100/70 blur-2xl"
        aria-hidden
      />
      <div className="px-6 pt-5 pb-4 border-b border-brand-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-gray-900 tracking-tight">Platform</h1>
            <p className="text-sm text-gray-500 mt-1">
              Connection health, authentication state, manual refresh, and disconnect workflow
            </p>
          </div>
          <Tag color="green-inverse" className="px-3 py-1 rounded-full font-medium">
            {tableData.length} connections
          </Tag>
        </div>
        <div className="mt-3 h-1.5 w-16 rounded-full bg-gradient-to-r from-emerald-400 to-brand-500" aria-hidden />
      </div>

      <div className="p-4 bg-white/70 backdrop-blur-[1px]">
        <Space wrap size={12}>
          {!activeClientId && (
            <Alert
              type="warning"
              showIcon
              message="No active client in session. Open Clients and pick a client first."
            />
          )}
          <Input
            placeholder="Name"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            suffix={<SearchOutlined className="text-brand-500" />}
            style={{ width: 180 }}
            allowClear
          />
          <Input
            placeholder="Company name"
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select<PlatformType | 'all'>
            value={typeFilter}
            onChange={setTypeFilter}
            options={[{ value: 'all', label: 'All platforms' }, ...typeOptions]}
            style={{ width: 180 }}
          />
          <Select<PlatformStatus | 'all'>
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Connected', label: 'Connected' },
              { value: 'Refreshing', label: 'Refreshing' },
              { value: 'Refreshed', label: 'Refreshed' },
              { value: 'Failed', label: 'Failed' },
            ]}
            style={{ width: 160 }}
          />
          <Tooltip title="Refresh table">
            <Button
              icon={<ReloadOutlined />}
              aria-label="Refresh platform table"
              onClick={() => {
                refetch()
                addToast('Platform list refreshed', 'success')
              }}
            />
          </Tooltip>
          {activeClientId && (
            <Tooltip title={advicePrepTrial.atLimit ? advicePrepTrialLimitHint : 'Add a new platform connection'}>
              <Button
                data-tour="platform-add"
                icon={<PlusOutlined />}
                disabled={advicePrepTrial.atLimit}
                onClick={openCreate}
              >
                Add Platform
              </Button>
            </Tooltip>
          )}
          {showAddXeroConnection(envName) && activeClientId && (
            <Tooltip
              title={
                advicePrepTrial.atLimit
                  ? advicePrepTrialLimitHint
                  : 'Open Xero bulk SSO to add one or more Xero connections for this client'
              }
            >
              <Button
                icon={<PlusOutlined />}
                disabled={advicePrepTrial.atLimit}
                onClick={() => {
                  const session = authSession
                  const userId = session?.currentUserId ?? ''
                  if (!userId) {
                    addToast('Session missing user ID', 'error')
                    return
                  }
                  const url = getXeroBulkSsoUrl(activeClientId, userId, envName)
                  const popup = openPopup(url, 600, 500)
                  if (!popup) {
                    addToast('Popup blocked. Allow popups and retry.', 'error')
                    return
                  }
                  addToast('Complete authentication in the popup window.', 'info')
                }}
              >
                Add Xero Connection
              </Button>
            </Tooltip>
          )}
          {isDiAdmin && activeClientId && (
            // Left open over the modal, this tooltip would be the one to take the Esc key.
            <Tooltip title={clientRefreshOpen ? undefined : 'Start a client level refresh and see past refreshes'}>
              <Button icon={<HistoryOutlined />} onClick={() => setClientRefreshOpen(true)}>
                Client Refresh
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Bulk disconnect selected">
            <Button
              danger
              icon={<DisconnectOutlined />}
              aria-label="Disconnect selected platforms"
              disabled={selectedRowKeys.length === 0}
              onClick={handleBulkDisconnectClick}
            />
          </Tooltip>
          {(platformTableLoading || initEditMutation.isPending) && (
            <span className="text-sm text-gray-500 inline-flex items-center gap-1.5">
              <span className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              Loading...
            </span>
          )}
        </Space>

        {advicePrepTrial.atLimit && (
          <div className="mt-4">
            <AdvicePrepTrialLimitAlert trialEnd={advicePrepTrial.trialEnd} />
          </div>
        )}
      </div>

      <div className="p-4">
        <Table<PlatformConnectionRow>
          rowKey="id"
          columns={columns}
          dataSource={tableData}
          loading={platformTableLoading}
          onChange={onTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: tableData.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50'],
            showTotal: (total, range) => `${range[0] ?? 0} - ${range[1] ?? 0} of ${total}`,
          }}
        />
      </div>

      <CreatePlatformModal
        open={createState.open}
        activeClientId={activeClientId}
        createTypeOptions={createTypeOptions}
        isPending={createPlatformMutation.isPending}
        onClose={closeCreate}
        onCreate={(values) => createPlatformMutation.mutateAsync(values)}
        addToast={addToast}
      />

      <BulkDisconnectProgressModal
        open={bulkProgressOpen}
        ids={bulkProgressIds}
        isBulk={bulkProgressIsBulk}
        idToName={bulkProgressIdToName}
        onClose={closeBulkProgress}
      />

      <ManualRefreshModal
        open={refreshState.open}
        row={refreshState.row}
        isPending={manualRefreshMutation.isPending}
        onClose={() => setRefreshState({ open: false, row: null })}
        onRun={(payload) => manualRefreshMutation.mutateAsync(payload)}
        addToast={addToast}
      />

      {isDiAdmin && activeClientId && (
        <ClientRefreshModal
          key={activeClientId}
          open={clientRefreshOpen}
          clientId={activeClientId}
          onClose={() => setClientRefreshOpen(false)}
          addToast={addToast}
        />
      )}

      <EditPlatformModal
        open={editState.open}
        row={editState.row}
        clientId={activeClientId}
        envName={envName}
        typeOptions={typeOptions}
        updatePending={updatePlatformMutation.isPending}
        deletePending={deletePlatformMutation.isPending}
        onClose={closeEdit}
        onUpdate={(values) => updatePlatformMutation.mutateAsync(values)}
        onDelete={(id) => deletePlatformMutation.mutateAsync(id)}
        onDisconnect={(id) => disconnectMutation.mutateAsync({ platformIds: [id], isBulk: false })}
        onRefetch={refetch}
        addToast={addToast}
      />
    </div>
  )
}
